/**
 * BannerCoverageWidget — Cover-page banner translation coverage panel.
 *
 * Renders on the /admin/i18n-status page below the overall summary.
 * Pulls /api/admin/contract-banners/status and shows a per-language
 * grid of "title" + "body" snippets with a green/red coverage badge.
 *
 * Actions:
 *   - "Rigenera mancanti"   → POST /api/admin/contract-banners/translate-all
 *                              (force=false; safe, skips cached entries)
 *   - "Re-traduci tutti"    → same endpoint with force=true (confirm prompt)
 *   - inline "🔄" button on each row → POST translate/{lang}
 *
 * The button-trigger flow is async (one Claude call per language ≈3-5s)
 * so we show a per-language spinner and disable the row until the
 * promise settles.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Languages,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Loader2,
  ZapOff,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";

const FLAGS = {
  en: "\u{1F1EC}\u{1F1E7}", fr: "\u{1F1EB}\u{1F1F7}", es: "\u{1F1EA}\u{1F1F8}",
  de: "\u{1F1E9}\u{1F1EA}", nl: "\u{1F1F3}\u{1F1F1}", pt: "\u{1F1F5}\u{1F1F9}",
  pl: "\u{1F1F5}\u{1F1F1}", sv: "\u{1F1F8}\u{1F1EA}", no: "\u{1F1F3}\u{1F1F4}",
  fi: "\u{1F1EB}\u{1F1EE}", da: "\u{1F1E9}\u{1F1F0}", cs: "\u{1F1E8}\u{1F1FF}",
  el: "\u{1F1EC}\u{1F1F7}", sk: "\u{1F1F8}\u{1F1F0}", hu: "\u{1F1ED}\u{1F1FA}",
  ro: "\u{1F1F7}\u{1F1F4}", bg: "\u{1F1E7}\u{1F1EC}", hr: "\u{1F1ED}\u{1F1F7}",
  sl: "\u{1F1F8}\u{1F1EE}", it: "\u{1F1EE}\u{1F1F9}",
};
const flag = (c) => FLAGS[(c || "").toLowerCase()] || "\u{1F310}";

export default function BannerCoverageWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAll, setBusyAll] = useState(false);
  const [busyLang, setBusyLang] = useState(null); // single-lang spinner

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/contract-banners/status`);
      setData(r.data);
    } catch (e) {
      toast.error(`Errore caricamento banner coverage: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const translateAll = async (force = false) => {
    if (force && !window.confirm(
      "Re-tradurre TUTTI i banner (anche quelli già cachati)?\n\n" +
      "Costo: ~19 chiamate Claude (~30-60 sec totali, ~0.005 EUR).\n" +
      "Da usare solo se hai cambiato il testo IT del disclaimer."
    )) return;
    setBusyAll(true);
    try {
      const r = await axios.post(
        `${API}/admin/contract-banners/translate-all`,
        null,
        { params: { force } }
      );
      const { ok = [], skipped = [], failed = {} } = r.data || {};
      const failedCount = Object.keys(failed).length;
      if (failedCount > 0) {
        toast.error(
          `Banner: ${ok.length} ok, ${skipped.length} skip, ${failedCount} falliti (${Object.keys(failed).join(", ")})`
        );
      } else if (ok.length === 0 && skipped.length > 0) {
        toast.info(`Tutti i ${skipped.length} banner sono già in cache. Nulla da fare.`);
      } else {
        toast.success(`Tradotti ${ok.length} banner${skipped.length ? ` (${skipped.length} già in cache)` : ""}.`);
      }
      await load();
    } catch (e) {
      toast.error(`Errore traduzione massiva: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyAll(false);
    }
  };

  const translateOne = async (lang) => {
    setBusyLang(lang);
    try {
      await axios.post(`${API}/admin/contract-banners/translate/${lang}`);
      toast.success(`Banner ${lang.toUpperCase()} rigenerato`);
      await load();
    } catch (e) {
      toast.error(`Errore ${lang.toUpperCase()}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyLang(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-gray-800 bg-[#111] p-6 mb-8 flex items-center justify-center gap-2 text-gray-500">
        <Loader2 size={18} className="animate-spin" /> Caricamento banner coverage…
      </div>
    );
  }
  if (!data) return null;

  const allCovered = data.missing.length === 0;
  const coveragePct = Math.round((data.covered_count / Math.max(data.total_count, 1)) * 100);

  return (
    <div
      className="rounded-2xl border border-gray-800 bg-[#111] mb-8 overflow-hidden"
      data-testid="banner-coverage-widget"
    >
      {/* Header */}
      <div
        className={`px-5 py-4 border-b border-gray-800 flex items-center gap-4 ${
          allCovered ? "bg-emerald-950/15" : "bg-lojack-clause-950/15"
        }`}
      >
        <div className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-gray-700 flex items-center justify-center text-gray-300">
          <Languages size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold">Banner cover PDF — traduzioni AI</h3>
            {allCovered ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 font-mono bg-emerald-900/30 border border-emerald-700/40 rounded-full px-2 py-0.5">
                <CheckCircle2 size={10} /> {data.covered_count}/{data.total_count} OK
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-lojack-clause-400 font-mono bg-lojack-clause-900/30 border border-lojack-clause-700/40 rounded-full px-2 py-0.5">
                <AlertTriangle size={10} /> {data.covered_count}/{data.total_count} ({coveragePct}%)
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Disclaimer &ldquo;traduzione preliminare&rdquo; sulla cover dei PDF tradotti.
            Pre-tradotto via Claude. Fallback EN+IT se mancante.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!allCovered && (
            <button
              onClick={() => translateAll(false)}
              disabled={busyAll}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-lojack-blue-600 hover:bg-lojack-blue-500 text-white transition disabled:opacity-50"
              data-testid="translate-missing-banners-btn"
            >
              {busyAll ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Traduci mancanti ({data.missing.length})
            </button>
          )}
          <button
            onClick={() => translateAll(true)}
            disabled={busyAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-[#1A1A1A] border border-gray-700 hover:border-lojack-clause-500 text-gray-300 hover:text-white transition disabled:opacity-50"
            title="Re-traduce TUTTI i banner. Usare solo se cambi il testo IT."
            data-testid="force-retranslate-all-banners-btn"
          >
            {busyAll ? <Loader2 size={12} className="animate-spin" /> : <ZapOff size={12} />}
            Force re-translate
          </button>
        </div>
      </div>

      {/* Italian source-of-truth callout */}
      <div className="px-5 py-3 bg-[#0F0F0F] border-b border-gray-800 text-[11px] text-gray-400 leading-relaxed">
        <span className="font-mono text-gray-500 uppercase tracking-wider text-[10px]">Sorgente IT</span>{" "}
        <span className="text-gray-300 font-medium">{data.italian_source.title}</span> —{" "}
        <span className="italic">{data.italian_source.body}</span>
      </div>

      {/* Language grid */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="banner-coverage-grid">
        {data.languages.map((row) => {
          const covered = row.has_title && row.has_body;
          const busy = busyLang === row.lang;
          return (
            <div
              key={row.lang}
              className={`bg-[#0A0A0A] border rounded-lg p-3 ${
                covered ? "border-gray-800" : "border-lojack-clause-700/50"
              }`}
              data-testid={`banner-row-${row.lang}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base">{flag(row.lang)}</span>
                  <span className="font-mono text-xs text-gray-300 uppercase">{row.lang}</span>
                  <span className="text-[11px] text-gray-500 truncate">{row.label}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {covered ? (
                    <CheckCircle2 size={12} className="text-emerald-400" />
                  ) : (
                    <AlertTriangle size={12} className="text-lojack-clause-400" />
                  )}
                  <button
                    onClick={() => translateOne(row.lang)}
                    disabled={busy || busyAll}
                    className="text-gray-500 hover:text-lojack-blue-300 transition disabled:opacity-30"
                    title={covered ? "Rigenera traduzione" : "Genera traduzione mancante"}
                    data-testid={`retranslate-${row.lang}-btn`}
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  </button>
                </div>
              </div>
              {covered ? (
                <p className="text-[11px] text-gray-400 leading-snug line-clamp-2" title={row.translated_at}>
                  <span className="text-gray-200 font-medium">{row.translated_at?.slice(0, 10)}</span>
                  {" · "}
                  <span className="font-mono text-[10px] text-gray-600">{row.model?.replace("anthropic/", "")}</span>
                </p>
              ) : (
                <p className="text-[11px] text-lojack-clause-300">Banner mancante — fallback EN+IT</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
